import { useCallback, useRef, useState } from "react";
import { getDocumentCacheKeys, getDocumentContentType } from "./documentUtils";
import { DocumentPanelItem, DocumentPreviewData } from "./types";

interface UseDocumentsPreviewOptions<T extends DocumentPanelItem> {
    fetchDocumentData?: (document: T) => Promise<DocumentPreviewData | null>;
}

export interface DocumentsPreviewController<T extends DocumentPanelItem> {
    fileType: string | null;
    preview: string | null;
    pdfData: string | null;
    numPages: number;
    scale: number;
    isLoading: boolean;
    previewedDocument: T | null;
    setScale: (scale: number) => void;
    setNumPages: (numPages: number) => void;
    markUnsupported: () => void;
    previewDocument: (document: T) => Promise<void>;
    cacheDocumentData: (document: DocumentPanelItem, data: DocumentPreviewData) => void;
    resetPreview: () => void;
}

export function useDocumentsPreview<T extends DocumentPanelItem>(
    { fetchDocumentData }: UseDocumentsPreviewOptions<T> = {}
): DocumentsPreviewController<T> {
    const [preview, setPreview] = useState<string | null>(null);
    const [pdfData, setPdfData] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number>(0);
    const [fileType, setFileType] = useState<string | null>(null);
    const [scale, setScale] = useState<number>(0.7);
    const [isLoading, setIsLoading] = useState(false);
    const [previewedDocument, setPreviewedDocument] = useState<T | null>(null);

    const cacheRef = useRef<Map<string, { data: string; contentType: string }>>(new Map());

    const markUnsupported = useCallback(() => setFileType("unsupported"), []);

    const resetPreview = useCallback(() => {
        setPreview(null);
        setPdfData(null);
        setNumPages(0);
        setFileType(null);
        setPreviewedDocument(null);
    }, []);

    const showPreviewData = useCallback((document: DocumentPanelItem, source: DocumentPreviewData) => {
        const rawData = source.data ?? "";
        const contentType = getDocumentContentType({ ...document, data: rawData, contentType: source.contentType ?? undefined });
        const isDataUrl = rawData.startsWith("data:");

        if (!contentType || !rawData) {
            setFileType("unsupported");
            return;
        }

        if (contentType.startsWith("image/")) {
            setPreview(isDataUrl ? rawData : `data:${contentType};base64,${rawData}`);
            setPdfData(null);
            setFileType("image");
            return;
        }

        if (contentType.startsWith("text/")) {
            setPreview(rawData);
            setPdfData(null);
            setFileType("text");
            return;
        }

        if (contentType === "application/pdf") {
            const base64 = isDataUrl ? rawData.split(",")[1] : rawData;
            setPdfData(`data:application/pdf;base64,${base64.replace(/\s+/g, "")}`);
            setPreview(null);
            setFileType("pdf");
            return;
        }

        setFileType("unsupported");
    }, []);

    const cacheDocumentData = useCallback((document: DocumentPanelItem, data: DocumentPreviewData) => {
        if (!data.data) return;
        const entry = { data: data.data, contentType: data.contentType ?? "application/octet-stream" };
        getDocumentCacheKeys(document).forEach((key) => cacheRef.current.set(key, entry));
    }, []);

    const previewDocument = useCallback(async (document: T) => {
        setPreviewedDocument(document);

        const cacheKeys = getDocumentCacheKeys(document);
        for (const key of cacheKeys) {
            const cached = cacheRef.current.get(key);
            if (cached?.data) {
                showPreviewData(document, cached);
                return;
            }
        }

        if (fetchDocumentData && document.id != null) {
            try {
                setIsLoading(true);
                const downloaded = await fetchDocumentData(document);
                if (!downloaded?.data) {
                    setFileType("unsupported");
                    return;
                }

                const contentType = downloaded.contentType ?? getDocumentContentType(document);
                showPreviewData(document, { data: downloaded.data, contentType });
                cacheDocumentData(document, { data: downloaded.data, contentType });
            } catch {
                setFileType("unsupported");
            } finally {
                setIsLoading(false);
            }
            return;
        }

        if (document.data) {
            showPreviewData(document, { data: document.data, contentType: document.contentType });
            return;
        }

        setFileType("unsupported");
    }, [cacheDocumentData, fetchDocumentData, showPreviewData]);

    return {
        fileType,
        preview,
        pdfData,
        numPages,
        scale,
        isLoading,
        previewedDocument,
        setScale,
        setNumPages,
        markUnsupported,
        previewDocument,
        cacheDocumentData,
        resetPreview,
    };
}
